import ClearIcon from "@assets/images/Clear.svg";
import SearchIconDark from "@assets/images/search-dark.svg";
import {
  Box,
  InputAdornment,
  Paper,
  PaperProps,
  TextField
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import useDebounce from "@saleor/hooks/useDebounce";
import { makeStyles } from "@saleor/macaw-ui";
import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";

import MyTypography from "../MyTypography";
import HandleRenderOption from "./RenderOption";

interface MakeStyleProps {
  contained?: boolean;
  isWidthAuto?: boolean;
  isSearchIcon?: boolean;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      "& .MuiAutocomplete-clearIndicator": {
        border: 0
      },
      "& .MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root']": {
        padding: "0 39px 0 0"
      },
      "& .MuiAutocomplete-inputRoot.Mui-disabled[class*='MuiOutlinedInput-root']": {
        padding: "0 39px 0 0"
      },
      '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input:first-child': {
        paddingLeft: "1.6rem"
      }
    },
    paper: {
      width: "100%",
      maxWidth: (props: MakeStyleProps) => props.contained && "100%",
      background: "white",
      boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)"
    },
    paperItem: {
      fontSize: "1.4rem",
      lineHeight: "2.2rem",
      color: theme.palette.grey[900],
      fontFamily: "ScgFont, sans-serif",
      width: "100%",
      boxShadow: "0 0 0 0",
      borderRadius: 0,
      '& li[data-focus="true"]': {
        color: theme.palette.info.main,
        backgroundColor: theme.palette.background.default
      },
      '& li[aria-selected="true"]': {
        color: theme.palette.info.main,
        backgroundColor: "#EBF1FF"
      },
      "& .MuiAutocomplete-listbox": {
        "&::-webkit-scrollbar": {
          height: "14px",
          background: "#FFFFFF"
        },
        "&::-webkit-scrollbar-track": {
          borderRadius: 10
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#CCCCCC",
          borderRadius: 10,
          border: "4px solid #FFFFFF",
          height: "6px"
        }
      }
    },
    paperHead: {
      background: "white",
      padding: 16
    },
    divider: {
      margin: "0 16px",
      borderTop: `1px solid ${theme.palette.grey[300]}`
    }
  }),
  { name: "Autocomplete" }
);

interface IAutoComplete {
  optionsProp: any[];
  className?: any;
  placeholderProps?: string;
  totalCount?: number;
  onChangeProps?: (value: string) => void;
  loading?: boolean;
  onSearch?: (value: string) => void;
  isReset?: boolean;
  resetAlways?: boolean;
  contained?: boolean;
  isWidthAuto?: boolean;
  isSearchIcon?: boolean;
  error?: boolean;
  objectSuggestion?: boolean;
  defaultInputValue?: string;
  defaultValue?: any;
  cleaned?: boolean;
  disabled?: boolean;
  notUseFilterOptions?: boolean;
  id?: string;
  testId?: string;
  name?: string;
  resetInputValueOnBlur?: boolean;
  onLastItemVisible?: (elId: string, position: number) => void;
  onMouseLeave?(event: any);
}

const AutoCompleteComponent: React.FC<IAutoComplete> = props => {
  const {
    optionsProp,
    placeholderProps,
    totalCount,
    onChangeProps,
    loading,
    onSearch,
    resetAlways,
    isReset,
    error,
    objectSuggestion,
    onMouseLeave,
    defaultInputValue,
    defaultValue,
    disabled,
    onLastItemVisible,
    notUseFilterOptions,
    id,
    testId,
    name,
    resetInputValueOnBlur = false
  } = props;
  const classes = useStyles(props);
  const inputRef = useRef<HTMLInputElement>(null);
  const firstRender = useRef(true);

  const [value, setValue] = useState<string>("");
  const [valueDebounce, setValueDebounce] = useState<string>("");
  const [valueSelected, setValueSelected] = useState<string | any>("");
  const [listEvent, setListEvent] = useState<any>(null);
  const [lastItemParams, setLastItemParams] = useState<any>(null);

  useEffect(() => {
    if (
      firstRender.current &&
      !value &&
      !valueDebounce &&
      !valueSelected &&
      defaultInputValue &&
      defaultValue
    ) {
      setValue(defaultInputValue);
      setValueDebounce(defaultInputValue);
      setValueSelected(defaultValue);
      firstRender.current = false;
    }
  }, [defaultInputValue, defaultValue, firstRender.current]);

  const valueDebounces = useDebounce(setValueDebounce, 500);
  const CustomPaper = (props: PaperProps) => {
    const checkDataMatchKeyword =
      props?.children?.[(props?.children as any[])?.length - 1];
    return (
      <Box className={classes.paper}>
        <Box className={classes.paperHead}>
          <MyTypography variant="body3" style={{ color: "#696969" }}>
            {!loading
              ? value.length >= 2 || valueSelected
                ? optionsProp.length && checkDataMatchKeyword
                  ? `Suggested searches (${totalCount || optionsProp.length})`
                  : `No results found`
                : "Please enter 2 or more characters"
              : ""}
          </MyTypography>
        </Box>
        {(value.length >= 2 || valueSelected) && checkDataMatchKeyword && (
          <>
            <Box className={classes.divider}></Box>
            <Paper elevation={8} {...props} className={classes.paperItem} />
          </>
        )}
      </Box>
    );
  };

  const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setValue(e.target.value || "");
  };

  const handleOnKeyPress: React.KeyboardEventHandler<HTMLDivElement> = e => {
    if (inputRef.current.value === "" && e.key === " ") {
      e.preventDefault();
    }
  };
  const handleScroll = (event: Event): void => {
    // handle load more records when scroll to bottom
    const lenOpts = optionsProp?.length || 0;
    if (
      !onLastItemVisible ||
      !totalCount ||
      !lenOpts ||
      lenOpts >= totalCount
    ) {
      // no need to load more
      return;
    }
    // cannot setState event.currentTarget
    setListEvent(event);
    if (!listEvent) {
      return;
    }
    const {
      id,
      scrollTop,
      clientHeight,
      scrollHeight
    } = listEvent.currentTarget;
    const posX = scrollTop + clientHeight;
    // is it bootom ?
    if (scrollHeight - posX <= 1) {
      setLastItemParams({ id, position: scrollTop });
    }
  };

  useEffect(() => {
    valueDebounces(value);
  }, [value]);

  useEffect(() => {
    if (onChangeProps) {
      onChangeProps(valueDebounce);
    }
  }, [valueDebounce]);

  useEffect(() => {
    if (onSearch) {
      onSearch(valueSelected || "");
    }
  }, [valueSelected]);

  useEffect(() => {
    if (isReset || resetAlways) {
      setValueSelected("");
      setValue("");
      setValueDebounce("");
    }
  }, [isReset]);

  useEffect(() => {
    // prevent double call onLastItemVisible
    const { id, position } = lastItemParams || {};
    if (!onLastItemVisible || !id || !position) {
      return;
    }
    onLastItemVisible(id, position);
  }, [lastItemParams?.position]);

  const extraProps = {
    ...(objectSuggestion
      ? {
          getOptionLabel: option => option?.title || "",
          options: optionsProp.map(option => option)
        }
      : {
          options: optionsProp.map(option => option?.title)
        }),
    ...(notUseFilterOptions && {
      filterOptions: option => option
    })
  };

  const SearchIcon = props.isSearchIcon
    ? {
        startAdornment: (
          <InputAdornment position="start">
            <div style={{ marginLeft: "8px" }}>
              <SVG src={SearchIconDark} width={24} height={24} />
            </div>
          </InputAdornment>
        )
      }
    : {};

  const [isResetOnBlur, setIsResetOnBlur] = useState<boolean>(false);

  return (
    <div className={classes.root}>
      <Autocomplete
        id={id}
        data-testid={testId}
        data-input-type="autocomplete-for-object-field"
        onChange={(_event, value, _reason) => {
          setValueSelected(value);
        }}
        {...extraProps}
        freeSolo
        onMouseLeave={onMouseLeave}
        key={`free-solo-demo-${isReset}-${isResetOnBlur}`}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholderProps}
            value={value}
            onChange={handleOnChange}
            onKeyPress={handleOnKeyPress}
            inputRef={inputRef}
            error={error}
            InputProps={{
              ...params.InputProps,
              ...SearchIcon
            }}
            inputProps={{
              ...params.inputProps,
              "data-testid": `${testId}-input`
            }}
            id={id}
            name={name}
          />
        )}
        closeIcon={<img src={ClearIcon} alt="" />}
        PaperComponent={CustomPaper}
        loading={loading}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "reset") {
            // setValue("");
            // setValueSelected("");
            // return;
          } else {
            setValue(newInputValue?.trim());
          }
        }}
        value={valueSelected || null}
        renderOption={HandleRenderOption}
        ListboxProps={{
          "data-testid": `${testId}-listbox`,
          style: { maxHeight: 280, overflow: "auto" },
          onScroll: handleScroll
        }}
        disabled={disabled}
        onBlur={() => {
          if (valueSelected?.title !== value && resetInputValueOnBlur) {
            setValue(valueSelected?.title || "");
            setIsResetOnBlur(prev => !prev);
          }
        }}
      />
    </div>
  );
};
export default AutoCompleteComponent;
